/**
 * Plain-English titles for Activity entries that record an edit to a record.
 *
 * An entry's stored label is the name of the button that was pressed
 * ("Change Dealer", "Resend documentation", "Edit registration"). A history
 * reads better as what HAPPENED: "Dealer changed", "Documentation resent",
 * "Registration edited"; a bare "Edit" reads "Details edited". The label is
 * tenant data (the partner noun is already resolved into it), so this only
 * reorders it around a past-tense verb and never names a tenant's thing.
 *
 * Only a leading verb from the table below is rewritten. A label whose verb is
 * not listed, or whose object starts with a preposition ("Send for approval",
 * "Place on hold"), would not survive the reorder, so it is returned unchanged.
 * So is a label that already reads as past tense ("Transfer cancelled",
 * "Ownership transferred"): there the first word is a noun, not a verb.
 */
import type { TFunction } from "i18next";

const PAST_TENSE: Record<string, string> = {
  add: "added",
  assign: "assigned",
  cancel: "cancelled",
  change: "changed",
  correct: "corrected",
  create: "created",
  delete: "deleted",
  edit: "edited",
  remove: "removed",
  reassign: "reassigned",
  resend: "resent",
  send: "sent",
  transfer: "transferred",
  update: "updated",
  upload: "uploaded",
};

const NOT_AN_OBJECT = new Set([
  "a",
  "an",
  "as",
  "back",
  "for",
  "from",
  "in",
  "into",
  "on",
  "the",
  "to",
  "with",
]);

const PAST_FORMS = new Set(Object.values(PAST_TENSE));

function readsAsPastTense(words: string[]): boolean {
  return words.some((word) => {
    const w = word.toLowerCase();
    return PAST_FORMS.has(w) || /[a-z]ed$/.test(w);
  });
}

export function pastTenseActivityTitle(label: string, t?: TFunction): string {
  const trimmed = label.trim();
  if (readsAsPastTense(trimmed.split(/\s+/).slice(1))) return trimmed;
  // A bare verb ("Edit") names no object, so say what it acted on in general.
  const lone = PAST_TENSE[trimmed.toLowerCase()];
  if (lone) {
    return t
      ? t("activity.detailsVerb", { defaultValue: "Details {{verb}}", verb: lone })
      : `Details ${lone}`;
  }
  const match = /^(\S+)\s+(.+)$/.exec(trimmed);
  if (!match) return trimmed;
  const [, verb, rest] = match;
  const past = PAST_TENSE[verb.toLowerCase()];
  const firstWord = rest.split(/\s+/)[0].toLowerCase();
  if (!past || NOT_AN_OBJECT.has(firstWord)) return trimmed;
  return `${rest[0].toUpperCase()}${rest.slice(1)} ${past}`;
}
